<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import {
  BookTemplate,
  Building,
  Building2,
  CalendarClock,
  FolderOpen,
  Handshake,
  LayoutDashboard,
  ListTodo,
  FileDown,
  LogOut,
  Menu,
  UserCog,
  Users,
  X,
} from '@lucide/vue';
import { useAuthStore } from '@/stores/auth';
import CampanellaNotifiche from '@/components/app/CampanellaNotifiche.vue';
import MarchioEvolfin from '@/components/app/MarchioEvolfin.vue';
import AvatarUtente from '@/components/ui/AvatarUtente.vue';

/**
 * Shell dell'applicazione: navigazione laterale + area contenuti.
 * La shell è ariosa; la densità sta nelle viste dati.
 */
const auth = useAuthStore();
const router = useRouter();

// `privilegi` nasconde la voce a chi non ha ALMENO UNO dei privilegi elencati
// (una sezione può avere più sotto-pagine gated da privilegi diversi) — la
// rotta resta comunque protetta dalla guardia del router e, per davvero, dal
// server. L'ordine segue la giornata di lavoro, non la mappa del dominio:
// prima dove si lavora (progetti e task), poi chi si consulta (rubrica e
// anagrafiche), infine quel che si configura di rado.
const tutteLeVoci = [
  { a: '/dashboard', etichetta: 'Dashboard', icona: LayoutDashboard },
  { a: '/progetti', etichetta: 'Progetti', icona: FolderOpen },
  // `esatta`: senza, `/task/team` accenderebbe anche «I miei task», perché il
  // match di RouterLink è inclusivo sul prefisso della rotta.
  { a: '/task', etichetta: 'I miei task', icona: ListTodo, esatta: true },
  {
    a: '/task/team',
    etichetta: 'Task del team',
    icona: CalendarClock,
    privilegi: ['can_read_all_task'] as const,
  },
  { a: '/rubrica', etichetta: 'Rubrica', icona: Users },
  { a: '/clienti', etichetta: 'Clienti', icona: Building2 },
  { a: '/partner', etichetta: 'Partner', icona: Handshake },
  {
    a: '/template',
    etichetta: 'Template',
    icona: BookTemplate,
    privilegi: ['can_manage_templates'] as const,
  },
  {
    a: '/utenti',
    etichetta: 'Utenti',
    icona: UserCog,
    privilegi: ['can_create_utenti', 'can_write_utenti', 'can_delete_utenti'] as const,
  },
  {
    a: '/impostazioni',
    etichetta: 'Impostazioni',
    icona: Building,
    privilegi: [
      'can_manage_impostazioni',
      'can_manage_tipi_documento_cliente',
      'can_manage_tipologie_cliente',
      'can_manage_promemoria',
      'can_read_audit_log',
    ] as const,
  },
];

const voci = computed(() =>
  tutteLeVoci
    .filter((v) => !('privilegi' in v) || (v.privilegi?.some((p) => auth.ha(p)) ?? true))
    .map((v) =>
      // "Impostazioni" ha più sotto-pagine gated da privilegi diversi: chi
      // non ha `can_manage_impostazioni` (la pagina Azienda, di default)
      // deve atterrare sulla prima sotto-pagina che può aprire, non su una
      // che il router le rimanderebbe indietro.
      v.a === '/impostazioni' ? { ...v, a: primaImpostazioneAccessibile() } : v,
    ),
);

/** Sotto-pagine di Impostazioni, nell'ordine della loro barra. */
const PAGINE_IMPOSTAZIONI = [
  { a: '/impostazioni', privilegio: 'can_manage_impostazioni' },
  { a: '/impostazioni/tipi-documento-cliente', privilegio: 'can_manage_tipi_documento_cliente' },
  { a: '/impostazioni/tipologie-cliente', privilegio: 'can_manage_tipologie_cliente' },
  { a: '/impostazioni/promemoria', privilegio: 'can_manage_promemoria' },
  { a: '/impostazioni/registro-attivita', privilegio: 'can_read_audit_log' },
] as const;

function primaImpostazioneAccessibile(): string {
  return PAGINE_IMPOSTAZIONI.find((p) => auth.ha(p.privilegio))?.a ?? '/impostazioni';
}

const CLASSI_VOCE_ATTIVA =
  'bg-[var(--surface-accent-soft)] font-medium text-ink shadow-[var(--shadow-card)]';

// Etichetta dell'utente: nome e cognome, con l'email come ripiego quando il
// nome non è impostato (es. principal di servizio via API key).
const nomeCompleto = computed(() => {
  const p = auth.principal;
  if (!p) return '';
  return [p.nome, p.cognome].filter(Boolean).join(' ').trim();
});
const etichettaUtente = computed(() => nomeCompleto.value || auth.principal?.email || '');

async function esci(): Promise<void> {
  await auth.logout();
  await router.push('/auth/login');
}

// Menu: fisso da `lg` in su, drawer con overlay sotto. Si
// chiude alla navigazione, con Esc, o al click sul backdrop; blocca lo scroll
// del body mentre è aperto, per non far scorrere due superfici insieme.
const menuAperto = ref(false);

function chiudiMenu(): void {
  menuAperto.value = false;
}

function onTastoEsc(evento: KeyboardEvent): void {
  if (evento.key === 'Escape') chiudiMenu();
}

watch(menuAperto, (aperto) => {
  document.body.style.overflow = aperto ? 'hidden' : '';
});

onMounted(() => window.addEventListener('keydown', onTastoEsc));
onUnmounted(() => {
  window.removeEventListener('keydown', onTastoEsc);
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="flex h-screen flex-col overflow-hidden lg:flex-row">
    <!-- Barra mobile: sostituisce l'intestazione della sidebar sotto `lg`,
         dove il menu diventa un drawer aperto dall'hamburger. -->
    <header
      class="flex items-center justify-between gap-2 border-b border-line-soft bg-soft px-3 py-2.5 lg:hidden"
    >
      <button
        type="button"
        class="rounded-sm p-2 text-ink-secondary transition-colors hover:bg-[var(--surface-accent-soft)] hover:text-ink"
        aria-label="Apri il menu"
        :aria-expanded="menuAperto"
        @click="menuAperto = true"
      >
        <Menu :size="20" :stroke-width="1.8" />
      </button>
      <RouterLink
        to="/dashboard"
        class="flex items-center gap-2 rounded-md px-1 py-1"
        aria-label="Evolfin — vai alla dashboard"
      >
        <MarchioEvolfin :dimensione="24" decorativo class="shrink-0" />
        <span
          class="font-display text-[17px] leading-none font-semibold tracking-[-0.03em] text-ink"
        >
          Evol<span class="text-[var(--color-primary)]">fin</span>
        </span>
      </RouterLink>
      <CampanellaNotifiche />
    </header>

    <!-- Backdrop: solo sotto `lg` e solo a drawer aperto. -->
    <div
      v-if="menuAperto"
      class="fixed inset-0 z-40 bg-black/40 lg:hidden"
      aria-hidden="true"
      @click="chiudiMenu"
    />

    <nav
      class="fixed inset-y-0 left-0 z-50 flex h-full w-72 -translate-x-full flex-col border-r border-line-soft bg-soft px-3 py-5 transition-transform duration-200 ease-out motion-reduce:transition-none lg:static lg:z-auto lg:w-60 lg:translate-x-0"
      :class="menuAperto && 'translate-x-0'"
    >
      <!-- Intestazione: identità dell'app + segnali globali (notifiche).
           Lockup marchio+wordmark (stesso marchio della pagina di login) al
           posto del testo isolato: il marchio àncora il nome, che porta
           l'accento sulla radice "fin" (finanza). Su mobile l'intestazione
           duplica quella della barra sopra, ma qui affianca anche la chiusura
           esplicita del drawer. -->
      <div class="mb-6 flex items-center justify-between gap-2 px-2">
        <RouterLink
          to="/dashboard"
          class="-mx-1 flex items-center gap-2.5 rounded-md px-1 py-1"
          aria-label="Evolfin — vai alla dashboard"
          @click="chiudiMenu"
        >
          <MarchioEvolfin :dimensione="28" decorativo class="shrink-0" />
          <span
            class="font-display text-[19px] leading-none font-semibold tracking-[-0.03em] text-ink"
          >
            Evol<span class="text-[var(--color-primary)]">fin</span>
          </span>
        </RouterLink>
        <div class="flex items-center gap-1">
          <CampanellaNotifiche />
          <button
            type="button"
            class="rounded-sm p-1.5 text-ink-muted transition-colors hover:text-ink lg:hidden"
            aria-label="Chiudi il menu"
            @click="chiudiMenu"
          >
            <X :size="18" :stroke-width="1.8" />
          </button>
        </div>
      </div>

      <!-- Navigazione: scorre da sé se le voci eccedono, così il blocco utente
           resta ancorato in fondo alla sidebar a tutta altezza. -->
      <div class="flex flex-1 flex-col gap-1 overflow-y-auto">
        <RouterLink
          v-for="voce in voci"
          :key="voce.a"
          :to="voce.a"
          class="flex items-center gap-2.5 rounded-sm px-3 py-2 text-ink-secondary transition-colors hover:bg-[var(--surface-accent-soft)]"
          :active-class="voce.esatta ? '' : CLASSI_VOCE_ATTIVA"
          :exact-active-class="CLASSI_VOCE_ATTIVA"
          @click="chiudiMenu"
        >
          <component :is="voce.icona" :size="17" :stroke-width="1.7" aria-hidden="true" />
          {{ voce.etichetta }}
        </RouterLink>
      </div>

      <div class="mt-4 flex items-center gap-1 border-t border-line-soft pt-4">
        <RouterLink
          to="/account"
          class="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-3 py-1.5 transition-colors hover:bg-[var(--surface-accent-soft)]"
          active-class="bg-[var(--surface-accent-soft)] shadow-[var(--shadow-card)]"
          title="Account e sicurezza"
        >
          <AvatarUtente
            :id="auth.principal?.id"
            :nome="auth.principal?.nome"
            :cognome="auth.principal?.cognome"
            :email="auth.principal?.email"
            :ha-immagine="auth.principal?.haImmagine"
            :dimensione="32"
          />
          <span class="min-w-0 flex-1 truncate text-xs text-ink-secondary" :title="etichettaUtente">
            {{ etichettaUtente }}
          </span>
        </RouterLink>
        <RouterLink
          to="/export"
          class="rounded-sm p-1.5 text-ink-muted transition-colors hover:text-ink"
          active-class="text-[var(--color-primary)]"
          aria-label="I miei export"
          title="I miei export"
          @click="chiudiMenu"
        >
          <FileDown :size="16" :stroke-width="1.7" />
        </RouterLink>
        <button
          type="button"
          class="rounded-sm p-1.5 text-ink-muted transition-colors hover:text-ink"
          aria-label="Esci"
          @click="esci"
        >
          <LogOut :size="16" :stroke-width="1.7" />
        </button>
      </div>
    </nav>

    <main class="min-w-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
      <RouterView />
    </main>
  </div>
</template>
